import { useEffect, useRef } from "react";

const FIELDS_BY_MODULE = Object.freeze({
  advancement: new Set(["candidateRefs", "amountCents", "targetCompetence"]),
  extra_value: new Set([
    "candidateRefs",
    "kind",
    "totalCents",
    "startCompetence",
    "installments",
    "description",
  ]),
});
const EMPTY_FIELDS = new Set();

const MAX_SAFE_CENTS = BigInt(Number.MAX_SAFE_INTEGER);
const MONEY_INPUT = /^(0|[1-9]\d*)(?:\.(\d{0,2}))?$/;

const moneyValue = (cents) => {
  if (!Number.isSafeInteger(cents) || cents < 0) return "";
  const exactCents = BigInt(cents);
  const whole = exactCents / 100n;
  const fraction = String(exactCents % 100n).padStart(2, "0");
  return `${whole}.${fraction}`;
};

const centsFromInput = (value) => {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (normalized.length > 17) return null;
  const match = MONEY_INPUT.exec(normalized);
  if (!match) return null;
  try {
    const cents =
      BigInt(match[1]) * 100n + BigInt((match[2] ?? "").padEnd(2, "0") || "0");
    return cents > 0n && cents <= MAX_SAFE_CENTS ? Number(cents) : null;
  } catch {
    return null;
  }
};

const AiActionFields = ({ draft, values, disabled = false, onChange }) => {
  const firstFieldRef = useRef(null);
  const fields = values ?? draft?.fields ?? {};
  const missingFields = Array.isArray(draft?.missingFields)
    ? draft.missingFields
    : [];
  const allowed = FIELDS_BY_MODULE[draft?.module] ?? EMPTY_FIELDS;
  const unknownFields = missingFields.filter((field) => !allowed.has(field));
  const renderableFields = missingFields.filter((field) => allowed.has(field));
  const firstField = renderableFields[0];
  const selectedRefs = Array.isArray(fields.selectedCandidateRefs)
    ? fields.selectedCandidateRefs
    : [];

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, [draft?.draftId, firstField]);

  const focusRef = (field, candidateIndex = 0) =>
    field === firstField && candidateIndex === 0 ? firstFieldRef : undefined;

  const changeCandidates = (ref, checked) => {
    const next = checked
      ? [...new Set([...selectedRefs, ref])]
      : selectedRefs.filter((selectedRef) => selectedRef !== ref);
    onChange("selectedCandidateRefs", next);
  };

  const candidateField = () => (
    <fieldset className="ai-action-fields__candidates" disabled={disabled}>
      <legend>Itens da EAP</legend>
      {(Array.isArray(draft?.candidates) ? draft.candidates : []).map(
        (candidate, index) => {
          const checked = selectedRefs.includes(candidate.ref);
          return (
            <label key={candidate.ref}>
              <input
                ref={focusRef("candidateRefs", index)}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(event) =>
                  changeCandidates(candidate.ref, event.target.checked)
                }
              />
              <span>{candidate.label}</span>
            </label>
          );
        },
      )}
    </fieldset>
  );

  const field = (fieldName) => {
    if (fieldName === "candidateRefs") return candidateField();
    if (fieldName === "amountCents" || fieldName === "totalCents") {
      const label =
        fieldName === "amountCents" ? "Valor a adiantar" : "Valor total";
      return (
        <label className="ai-action-fields__field">
          <span>{label}</span>
          <input
            ref={focusRef(fieldName)}
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            value={moneyValue(fields[fieldName])}
            disabled={disabled}
            onChange={(event) =>
              onChange(fieldName, centsFromInput(event.target.value))
            }
          />
        </label>
      );
    }
    if (fieldName === "targetCompetence" || fieldName === "startCompetence") {
      const label =
        fieldName === "targetCompetence" ? "Mês de destino" : "Mês inicial";
      return (
        <label className="ai-action-fields__field">
          <span>{label}</span>
          <input
            ref={focusRef(fieldName)}
            type="month"
            value={fields[fieldName] ?? ""}
            disabled={disabled}
            onChange={(event) =>
              onChange(fieldName, event.target.value || null)
            }
          />
        </label>
      );
    }
    if (fieldName === "kind") {
      return (
        <label className="ai-action-fields__field">
          <span>Tipo do ajuste</span>
          <select
            ref={focusRef(fieldName)}
            value={fields.kind ?? ""}
            disabled={disabled}
            onChange={(event) => onChange("kind", event.target.value || null)}
          >
            <option value="">Selecione</option>
            <option value="expense">Despesa</option>
            <option value="saving">Economia</option>
          </select>
        </label>
      );
    }
    if (fieldName === "installments") {
      return (
        <label className="ai-action-fields__field">
          <span>Número de parcelas</span>
          <input
            ref={focusRef(fieldName)}
            type="number"
            inputMode="numeric"
            min="1"
            max="36"
            step="1"
            value={fields.installments ?? ""}
            disabled={disabled}
            onChange={(event) => {
              const value = Number(event.target.value);
              onChange(
                "installments",
                Number.isSafeInteger(value) && value >= 1 && value <= 36
                  ? value
                  : null,
              );
            }}
          />
        </label>
      );
    }
    if (fieldName === "description") {
      return (
        <label className="ai-action-fields__field ai-action-fields__field--wide">
          <span>Descrição</span>
          <textarea
            ref={focusRef(fieldName)}
            maxLength={500}
            value={fields.description ?? ""}
            disabled={disabled}
            onChange={(event) =>
              onChange("description", event.target.value || null)
            }
          />
        </label>
      );
    }
    return null;
  };

  return (
    <section
      className="ai-action-fields"
      aria-label="Informações necessárias para a proposta"
    >
      {unknownFields.map((fieldName) => (
        <p
          className="ai-action-fields__unsupported"
          role="alert"
          key={fieldName}
        >
          Campo de ação não reconhecido: {fieldName}. Atualize a página antes de
          continuar.
        </p>
      ))}
      <div className="ai-action-fields__grid">
        {renderableFields.map((fieldName) => (
          <div
            className={`ai-action-fields__item${
              fieldName === "candidateRefs" || fieldName === "description"
                ? " ai-action-fields__item--wide"
                : ""
            }`}
            key={fieldName}
          >
            {field(fieldName)}
          </div>
        ))}
      </div>
    </section>
  );
};

export { centsFromInput, moneyValue };
export default AiActionFields;
